/**
 * Platform-agnostic native bridge. Use this when calling from web to native.
 * - Android: window.Android is provided by addJavascriptInterface
 * - iOS: window.Android is provided by polyfill (forwards to webkit.messageHandlers)
 *
 * Prefer: if (window.Android?.method) window.Android.method(...)
 * Or use: callNative('method', ...args) for explicit platform handling.
 */
type AndroidMethod = keyof Android

export function isNativeEmbedded(): boolean {
  return !!(window.Android || (window.webkit && window.webkit.messageHandlers))
}

/**
 * Async: get security (base64 proto) from native. Use with await.
 * - Android: window.AndroidDataBridge.getSecurity is sync
 * - iOS: postMessage returns a Promise natively (WKScriptMessageHandlerWithReply)
 */
export function getSecurityFromNative(exchange: string, code: string): Promise<string> {
  if (window.AndroidDataBridge?.getSecurity) {
    return Promise.resolve(window.AndroidDataBridge.getSecurity(exchange, code))
  }
  const handler = window.webkit?.messageHandlers?.getSecurity
  if (handler) {
    const p = handler.postMessage({ exchange: String(exchange), code: String(code || '') })
    return Promise.resolve(p).then((r: unknown) => (typeof r === 'string' ? r : '')).catch(() => '')
  }
  return Promise.resolve('')
}

/** Call native method. Uses Android (or iOS polyfill) if available, else webkit.messageHandlers directly. */
export function callNative<K extends AndroidMethod>(
  method: K,
  ...args: Parameters<Android[K]>
): void {
  if (window.Android?.[method]) {
    // Android or iOS (polyfill creates Android)
    ;(window.Android[method] as (...a: unknown[]) => void)(...args)
  } else if (window.webkit?.messageHandlers?.[method]) {
    // iOS fallback: direct webkit call (before polyfill or if polyfill didn't run)
    const payload = args.length <= 1 ? args[0] : args
    window.webkit.messageHandlers[method].postMessage(payload)
  }
}
